import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import ProfileHeader from "../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { routes } from "../../../../constants/routes";
import {
  getSupportTicketDetails,
  SupportTicket,
} from "../../../../api/admin/communication-management";
import { useSetLoading } from "../../../../common/hooks/appearance";
import "./view-ticket-details.scss";
import { AlertComponent } from "../../../../common/components";
import { Box, Typography, TextField } from "@mui/material";
import TicketAttachments from "./view-ticket-attachments/ticket-attachments";

const ViewTicketDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<SupportTicket | null>(null);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const ticketId =
    data?.ticketId ||
    data?.ticket_id ||
    (id ? `SUP${id}`.padStart(10, "0") : "");

  const breadcrumbLink = useMemo(
    () => [
      {
        label: "Support",
        link: routes.ADMIN_COMMUNICATION_MANAGEMENT,
        stateData: {
          searchText: location.state?.searchText || "",
          activeTab: location.state?.activeTab || 0,
        },
      },
      {
        label: ticketId || "Ticket Details",
        link: "#",
      },
    ],
    [ticketId, location.state],
  );

  const fetchTicketDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getSupportTicketDetails(id);

      if (response?.status === 200 || response?.data?.success) {
        const apiData =
          response?.data?.data?.data?.supportRequests?.[0] ||
          response?.data?.data ||
          {};

        const mappedTicket = {
          ...apiData,
          ticketId: apiData.ticket_id || apiData.ticketId,
          providerName: apiData.partner?.businessName || apiData.name,
          contactEmail:
            apiData.contact_email ||
            apiData.contactEmail ||
            apiData.partner?.email,
          contactNumber: apiData.contact_number || apiData.contactNumber,
          detailedDescription:
            apiData.description || apiData.detailedDescription,
          createdAt: apiData.created_at || apiData.createdAt,
        };

        setData(mappedTicket);
      } else {
        setAlertMessage("Failed to fetch ticket details.");
        setAlertType("error");
        setAlertOpen(true);
      }
    } catch (err: any) {
      setAlertMessage(
        err?.response?.data?.message ||
          "An error occurred while fetching data.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicketDetails();
  }, [id]);
  const attachments = useMemo(() => {
    if (!data) return [];

    let attachmentList: any[] = [];

    if (Array.isArray(data.attachments)) {
      attachmentList = data.attachments;
    } else if (data.attachmentUrl) {
      attachmentList = Array.isArray(data.attachmentUrl)
        ? data.attachmentUrl
        : [data.attachmentUrl];
    }

    return attachmentList.filter((att) => att != null);
  }, [data]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <Box>
      <ProfileHeader breadcrumbLink={breadcrumbLink} />
      <Box className="ticket-details">
        {loading && !data ? (
          <Typography>Loading ticket details...</Typography>
        ) : data ? (
          <>
            <Typography
              variant="h4"
              className="ticket-details-title"
              sx={{ borderBottom: "none", pb: 0 }}
            >
              Ticket Details
            </Typography>

            <div className="ticket-details-section">
              <div className="form-field">
                <label>Name</label>
                <TextField
                  fullWidth
                  value={data?.providerName || data?.name || "-"}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  size="small"
                  className="readonly-field"
                />
              </div>

              <div className="form-field">
                <label>Contact Email</label>
                <TextField
                  fullWidth
                  value={data?.contactEmail || "-"}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  size="small"
                  className="readonly-field"
                />
              </div>

              <div className="form-field">
                <label>Contact Number</label>
                <Box
                  sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
                  className="phone-number-wrapper"
                >
                  <TextField
                    value={data?.contactNumber || "-"}
                    InputProps={{
                      readOnly: true,
                    }}
                    variant="outlined"
                    size="small"
                    className="readonly-field"
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                      },
                    }}
                  />
                </Box>
              </div>

              <div className="form-field full-width">
                <label>Subject</label>
                <TextField
                  fullWidth
                  value={data?.subject || "-"}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  size="small"
                  className="readonly-field"
                />
              </div>

              <div className="form-field full-width">
                <label>Detail Description</label>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={data?.detailedDescription || "-"}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  size="small"
                  className="readonly-field"
                />
              </div>
            </div>
            {attachments && attachments.length > 0 && (
              <div className="attachments-wrapper">
                <p className="attachment-title">Attachments</p>
                <TicketAttachments attachmentList={attachments} />
              </div>
            )}
          </>
        ) : (
          <Typography>No ticket details found.</Typography>
        )}
      </Box>
      <AlertComponent
        open={alertOpen}
        message={alertMessage || ""}
        type={alertType}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
};

export default ViewTicketDetails;
